import {isEmpty} from 'lodash';
import React, {ReactNode} from 'react';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {Renderer} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {BoxedInputInheritableProps} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {LabeledInputDropdown} from '../dropdowns/labeledInputDropdown';
import {
  ResourceMultiSelectFilterDropdown,
  ResourceMultiselectFilterDropdownBaseProps,
} from '../filters/resourceMultiSelectFilterDropdown';

/*
 * Props.
 */

export interface ResourceMultiSelectInputProps<T> extends ResourceMultiselectFilterDropdownBaseProps<T> {
  label?: BoxedInputInheritableProps['label'];
  /** Used as dropdown box value when the input is empty. */
  renderPlaceholder: Renderer;
  /** Used as dropdown box value when the input is not empty. */
  renderValue: Renderer;
  isDisabled: BoxedInputInheritableProps['isDisabled'];
  isRequired: BoxedInputInheritableProps['isRequired'];
  messageForDisabled?: ReactNode;
  hasError?: boolean;
  errorMessage?: ReactNode;
}

/*
 * Component.
 */

/** A form input component for selecting multiple of a resource. */
export const ResourceMultiSelectInput = <T,>(props: ResourceMultiSelectInputProps<T>) => {
  const {
    dropdownTitle,
    dropdownSource,
    computeFilterGenerator,
    selectedItemIds: previousSelectedItemIds,
    onSetItems,
    onClearAll,
    isDisabled,
    isRequired,
    label,
    renderPlaceholder,
    renderValue,
    messageForDisabled,
    hasError,
    errorMessage,
  } = props;
  const renderDropdown = (popoverTransferProps: PopoverTransferProps) => (
    // TODO: decouple from filter components, as those aren't intended as form inputs like this component.
    <ResourceMultiSelectFilterDropdown
      {...popoverTransferProps}
      dropdownTitle={dropdownTitle}
      dropdownSource={dropdownSource}
      selectedItemIds={previousSelectedItemIds}
      computeFilterGenerator={computeFilterGenerator}
      onSetItems={onSetItems}
      onClearAll={onClearAll}
      clearButtonContent={<Trans id="resource-multi-select-input-clear-all">Clear all</Trans>}
      shouldAutoApply
      searchPlaceholder="Search"
      height={342}
      shouldStayOpenOnClear
      rightIcon={props.rightIcon}
    />
  );

  return (
    <LabeledInputDropdown
      label={label}
      renderPlaceholder={renderPlaceholder}
      renderValue={renderValue}
      renderDropdown={renderDropdown}
      isDisabled={isDisabled}
      isRequired={isRequired}
      messageForDisabled={messageForDisabled}
      hasError={hasError}
      errorMessage={errorMessage}
      hasPreviousSelection={!isEmpty(previousSelectedItemIds)}
    />
  );
};
